import { useState } from "react";
import { Link } from "react-router-dom";
import { Wand2, Eye, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { AdminTitle } from "./shared";

const MODELS = {
  openai: ["gpt-5.4-mini", "gpt-5.4", "gpt-5.2"],
  gemini: ["gemini-3.1-pro-preview", "gemini-3.5-flash"],
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-8"],
};

const EXAMPLES = [
  "Change the homepage hero headline to mention fall wedding season",
  "Add a new Drop & Geaux menu item: Shrimp & Grits Family Pan, feeds 10",
  "Write a blog post about catering the Billfish Tournament in Venice",
  "Update the footer text to mention we now serve Baton Rouge",
];

export default function AiUpdater() {
  const [instruction, setInstruction] = useState("");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.4-mini");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    setApplied(null);
    try {
      const { data } = await api.post("/admin/ai-edit", { instruction, provider, model }, { timeout: 180000 });
      setResult(data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-edit/apply", { proposal: result.proposal });
      setApplied(data);
      toast.success("Changes applied to staging");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    const { data } = await api.post("/admin/publish");
    toast.success(`Live! ${JSON.stringify(data.published)}`);
  };

  return (
    <div className="max-w-3xl" data-testid="ai-updater">
      <AdminTitle eyebrow="Type It, Ship It" title="AI Site Updater" />
      <div className="rounded-sm border border-brass/10 bg-card p-6 space-y-5">
        <div>
          <label className="admin-label">What should change on the site?</label>
          <textarea rows={5} value={instruction} onChange={(e) => setInstruction(e.target.value)} className="admin-input" data-testid="ai-instruction-input"
            placeholder="e.g. Add a holiday catering section to the homepage and change the hero subtitle to mention Christmas parties…" />
        </div>
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="admin-label">Provider</label>
            <select value={provider} onChange={(e) => { setProvider(e.target.value); setModel(MODELS[e.target.value][0]); }} className="admin-input" data-testid="ai-provider-select">
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini</option>
              <option value="anthropic">Claude</option>
            </select>
          </div>
          <div>
            <label className="admin-label">Model</label>
            <select value={model} onChange={(e) => setModel(e.target.value)} className="admin-input" data-testid="ai-model-select">
              {MODELS[provider].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <button onClick={run} disabled={busy || !instruction.trim()} data-testid="ai-run-button" className="flex items-center gap-2 rounded-full bg-brass px-8 py-3 text-xs font-semibold uppercase tracking-widest text-cast transition-colors hover:bg-flame hover:text-cream disabled:opacity-40">
          <Wand2 size={14} /> {busy ? "Thinking… (up to a minute)" : "Preview Changes"}
        </button>
        <p className="text-xs text-clay">Nothing is touched until you review the preview and apply it.</p>
      </div>

      <div className="mt-6 rounded-sm border border-brass/10 bg-card p-6">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass mb-3">Try one of these</p>
        <div className="flex flex-col gap-2">
          {EXAMPLES.map((ex, i) => (
            <button key={i} onClick={() => setInstruction(ex)} data-testid={`ai-example-${i}`} className="text-left text-sm text-clay hover:text-cream transition-colors">"{ex}"</button>
          ))}
        </div>
      </div>

      {result && !applied && (
        <div className="mt-6 rounded-sm border border-brass/30 bg-brass/5 p-6" data-testid="ai-result">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">Proposed Changes — not applied yet</p>
          <p className="mt-2 text-sm text-cream" data-testid="ai-summary">{result.summary}</p>
          <ul className="mt-4 space-y-2" data-testid="ai-diff-list">
            {(result.diff || []).map((line, i) => (
              <li key={i} className="flex gap-3 text-sm text-clay"><span className="font-mono text-brass shrink-0">{String(i + 1).padStart(2, "0")} /</span> {line}</li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={apply} disabled={busy} data-testid="ai-apply-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors disabled:opacity-40">
              <Wand2 size={14} /> {busy ? "Applying…" : "Apply to Staging"}
            </button>
            <button onClick={() => setResult(null)} data-testid="ai-discard-button" className="rounded-full border border-border px-6 py-2.5 text-xs uppercase tracking-widest text-clay hover:text-cream">
              Discard
            </button>
          </div>
        </div>
      )}

      {applied && (
        <div className="mt-6 rounded-sm border border-brass/30 bg-brass/5 p-6" data-testid="ai-applied">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">Applied to Staging</p>
          <p className="mt-2 text-sm text-cream" data-testid="ai-applied-summary">{applied.summary}</p>
          <p className="mt-1 text-xs font-mono text-clay">{JSON.stringify(applied.applied)}</p>
          <div className="mt-5 flex gap-3">
            <Link to="/staging" target="_blank" data-testid="ai-preview-staging" className="flex items-center gap-2 rounded-full border border-brass/40 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-brass hover:bg-brass/10">
              <Eye size={14} /> Preview Staging
            </Link>
            <button onClick={publish} data-testid="ai-publish-button" className="flex items-center gap-2 rounded-full bg-brass px-5 py-2 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream">
              <Rocket size={14} /> Publish to Live
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
